<template>
  <div 
    v-if="shouldShowQuestion && (answer || currentQuestionId == property.id)" 
    class="question-list-item"
    :class="{ 'item-active': isActive, 'item-answered': answer }"
    @click="editAnswer"
  >
    <div class="item-wrapper">
      <!-- Status Badge "status-badge" :class="{ 'badge-active': isActive, 'badge-answered': answer }-->
      <div class="">
        <svg v-if="answer" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="currentColor">
          <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>

      <!-- Question Content -->
      <div class="question-content">
        <div class="question-text">
          <ul v-if="property.question.includes('\n')" class="question-list">
            <li v-for="(line, index) in property.question.split('\n')" :key="'line-' + index">
              {{ line }}
            </li>
          </ul>
          <span v-else>{{ property.question }}</span>
        </div>

        <!-- Answer Display -->
        <div class="answer-display" v-if="hasText || hasTexts">
          <div class="answer-content">
            <!-- Text Answer -->
            <span v-if="hasText" class="answer-text">
              {{ answerText }}
            </span>

            <!-- Texts Answer (Array) -->
            <div v-if="hasTexts" class="answer-texts">
              <span v-if="shouldShowHoverHint" class="hover-hint" :title="textsTooltip">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clip-rule="evenodd"/>
                </svg>
                Hover to see answer
              </span>
              <span v-else class="answer-value">{{ getDisplayText() }}</span>
              
              <span v-if="hasMultipleTexts" class="count-badge">
                +{{ additionalTextsCount }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Button - Only show when question is answered -->
      <button v-if="answer" class="btn-edit" @click="editAnswer">
        <img 
          src="../../assets/editing.png" 
          alt="Edit" 
          class="btn-icon"
        />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Property } from "@/entities/questionaire/Property";
import AppBooleanAnswer from "@/components/expert/answer-types/AppBooleanAnswer.vue";
import AppSingleItemAnswer from "@/components/expert/answer-types/AppSingleItemAnswer.vue";
import AppMultipleItemsAnswer from "@/components/expert/answer-types/AppMultipleItemsAnswer.vue";
import { Answer } from "@/entities/questionaire/answers/Answer";
import { SingleItemAnswer } from "@/entities/questionaire/answers/SingleItemAnswer";

@Component({
  components: {
    AppBooleanAnswer,
    AppSingleItemAnswer,
    AppMultipleItemsAnswer,
  },
})
export default class AppQuestionListItem extends Vue {
  answerType!: string;

  @Prop()
  property!: Property;

  @Prop()
  isActive!: boolean;

  @Prop()
  currentQuestionId!: number;

  answer: Answer | null = null;

  get shouldShowQuestion(): boolean {
    return !this.property.autoAnswer;
  }

  get hasText(): boolean {
    return !!(this.answer && 'text' in this.answer && (this.answer as any).text);
  }

  get answerText(): string {
    if (this.hasText) {
      return (this.answer as any).text;
    }
    return '';
  }

  get hasTexts(): boolean {
    return !!(this.answer && 'texts' in this.answer && 
             (this.answer as any).texts && 
             (this.answer as any).texts.length > 0);
  }

  get firstText(): string {
    if (this.hasTexts) {
      return (this.answer as any).texts[0];
    }
    return '';
  }

  get textsTooltip(): string {
    if (this.hasTexts && this.firstText.length > 50) {
      if (this.property.questionSubType === "HowMany - SelectOne Range" || 
          this.property.questionSubType === "HowMany - YesNo") {
        return this.getDisplayText();
      }
      return this.firstText;
    }
    return '';
  }

  get shouldShowHoverHint(): boolean {
    if (this.property.questionSubType === "HowMany - SelectOne Range" || 
        this.property.questionSubType === "HowMany - YesNo") {
      return false;
    }
    return this.hasTexts && this.firstText.length > 50;
  }

  get hasMultipleTexts(): boolean {
    return this.hasTexts && (this.answer as any).texts.length > 1;
  }

  get additionalTextsCount(): number {
    if (this.hasMultipleTexts) {
      return (this.answer as any).texts.length - 1;
    }
    return 0;
  }

  created(): void {
    this.answerType = this.property.answer.getComponentName();
  }

  mounted(): void {
    this.$events.on("answerChanged", this.setAnswer);
  }

  beforeDestroy() {
    // this.$events.off("answerChanged");
    this.$events.off("throwError");
  }

  setAnswer(answer: Answer): void {
    if (answer.PropertyId == this.property.id) {
      this.answer = answer;
      this.property.selectedAnswer = answer;
    }
    this.$emit("setAnswer", answer);
  }

  getDisplayText(): string {
    if (!this.answer) {
      return '';
    }
    
    const answerWithTexts = this.answer as any;
    if (!answerWithTexts.texts || answerWithTexts.texts.length === 0) {
      return '';
    }
    
    if (this.property.questionSubType === "HowMany - SelectOne Range" || 
        this.property.questionSubType === "HowMany - YesNo") {
      if ('answer' in this.answer && typeof (this.answer as any).answer === 'number') {
        return `Score: ${(this.answer as any).answer}`;
      }
      try {
        const selections = JSON.parse(answerWithTexts.texts[0]);
        if (typeof selections === 'object' && selections !== null) {
          const total = Object.values(selections).reduce((sum: number, val: any) => {
            return sum + (Number(val) || 0);
          }, 0);
          return `Score: ${total}`;
        }
      } catch (e) {
        return 'Score: N/A';
      }
    }
    
    return answerWithTexts.texts[0];
  }

  editAnswer(): void {
    if (this.property.autoAnswer) {
      return;
    }

    this.$emit("currentQuestion", this.property);
    this.$store.commit('setDeleteExcludeRecommendations', true);
    this.$store.commit('setDeleteStore', true);

    if (window.innerWidth <= 768) {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.question-list-item {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }
}

.item-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(226, 232, 240, 0.5);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(248, 250, 252, 1);
    border-color: rgba(102, 123, 242, 0.3);
    transform: translateX(4px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
}

.item-active .item-wrapper {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(52, 211, 153, 0.05));
  border-color: #10b981;
  border-width: 2px;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.15);
}

.item-answered .item-wrapper {
  border-color: rgba(102, 123, 242, 0.2);
}

/* Status Badge */
.status-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(226, 232, 240, 0.3);
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
    color: #94a3b8;
  }
}

.badge-answered {
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.1), rgba(47, 69, 165, 0.05));

  svg {
    color: #2f45a5;
  }
}

.item-active .status-badge {
  background: linear-gradient(135deg, #10b981, #34d399);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);

  svg {
    color: white;
  }
}

/* Question Content */
.question-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.question-text {
  font-size:1.12rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.5;
  word-break: break-word;
  max-height: 120px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 2px;

    &:hover {
      background-color: #94a3b8;
    }
  }
}

.question-list {
  list-style: none;
  padding-left: 0;
  margin: 0;

  li {
    margin-bottom: 0.25rem;
    padding-left: 1rem;
    position: relative;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #667bf2;
      font-weight: bold;
    }
  }
}

/* Answer Display */
.answer-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.answer-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.answer-text,
.answer-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  padding: 0.375rem 0.75rem;
  background: rgba(102, 123, 242, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(102, 123, 242, 0.15);
}

.answer-texts {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hover-hint {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #667bf2;
  padding: 0.375rem 0.75rem;
  background: rgba(102, 123, 242, 0.05);
  border: 1px dashed rgba(102, 123, 242, 0.3);
  border-radius: 6px;
  cursor: help;
  transition: all 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(102, 123, 242, 0.1);
    border-color: #667bf2;
  }
}

.count-badge {
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
}

/* Edit Button */
.btn-edit {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(102, 123, 242, 0.1);
  border: 1px solid rgba(102, 123, 242, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  .btn-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }

  &:hover {
    background: rgba(102, 123, 242, 0.15);
    border-color: #2f45a5;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .item-wrapper {
    padding: 1rem;
    gap: 0.75rem;
  }

  .status-badge {
    width: 28px;
    height: 28px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .question-text {
    font-size: 0.9rem;
  }

  .answer-text,
  .answer-value {
    font-size: 0.8125rem;
    padding: 0.3rem 0.625rem;
  }

  .btn-edit {
    width: 26px;
    height: 26px;

    .btn-icon {
      width: 14px;
      height: 14px;
    }
  }
}

@media (max-width: 480px) {
  .item-wrapper {
    gap: 0.75rem;
    padding: 0.875rem;
  }

  .status-badge {
    width: 26px;
    height: 26px;

    svg {
      width: 15px;
      height: 15px;
    }
  }

  .question-content {
    width: 100%;
  }

  .question-text {
    font-size: 0.875rem;
  }

  .answer-display {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-edit {
    width: 24px;
    height: 24px;

    .btn-icon {
      width: 12px;
      height: 12px;
    }
  }

  .hover-hint {
    font-size: 0.75rem;
    padding: 0.3rem 0.625rem;
  }

  .count-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
}

/* Accessibility */
.item-wrapper:focus {
  outline: 2px solid #2f45a5;
  outline-offset: 2px;
}

.item-wrapper:focus:not(:focus-visible) {
  outline: none;
}
</style>